import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import Page from './Page';
import Header from './Header';
import Calendar from './Calendar';
import { useCatalog } from './contexts/CatalogContext.jsx';
import { useCart } from './contexts/CartContext.jsx';
import { fetchProduct } from './api';
import { holidays } from './data/holidays';

const formatPrice = (cents = 0, currency = 'USD') => {
  const value = (cents || 0) / 100;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  });
  return formatter.format(value);
};

const buildGallery = product => {
  if (!product) return [];
  const urls = [];
  if (product.featured_image_url) urls.push(product.featured_image_url);
  (product.images || []).forEach(image => {
    if (image.url && !urls.includes(image.url)) {
      urls.push(image.url);
    }
  });
  if (product.hero_image_url && !urls.includes(product.hero_image_url)) {
    urls.push(product.hero_image_url);
  }
  if (urls.length === 0) {
    urls.push('https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=900&q=60');
  }
  return urls;
};

const ProductShowPage = () => {
  const { slug } = useParams();
  const { products } = useCatalog();
  const { addItem } = useCart();
  const [product, setProduct] = useState(() => products.find(p => p.slug === slug));
  const [loading, setLoading] = useState(!product);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [rangeError, setRangeError] = useState(null);
  const [status, setStatus] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!product && products.length) {
      const match = products.find(p => p.slug === slug);
      if (match) {
        setProduct(match);
        setSelectedImage(buildGallery(match)[0]);
      }
    }
  }, [product, products, slug]);

  useEffect(() => {
    if (product) {
      setSelectedImage(buildGallery(product)[0]);
      return;
    }

    let isMounted = true;
    setLoading(true);
    fetchProduct(slug)
      .then(data => {
        if (!isMounted) return;
        setProduct(data);
        setSelectedImage(buildGallery(data)[0]);
        setLoading(false);
      })
      .catch(err => {
        console.error('Unable to load product', err);
        if (!isMounted) return;
        setError('We could not find that product.');
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [product, slug]);

  useEffect(() => {
    if (product) {
      setSelectedImage(buildGallery(product)[0]);
    }
  }, [product]);

  const galleryImages = useMemo(() => buildGallery(product), [product]);

  const handleQuantityChange = event => {
    const value = parseInt(event.target.value, 10);
    if (Number.isNaN(value) || value < 1) {
      setQuantity(1);
    } else {
      setQuantity(value);
    }
  };

  const handleCalendarConfirm = (start, end, opts = {}) => {
    const range = { startDate: start, endDate: end };
    setSelectedRange(range);
    setRangeError(null);
    setStatus(null);

    if (opts?.source === 'dialog') {
      handleAddToCart(range);
    }
  };

  const handleAddToCart = async (overrideRange) => {
    if (!product) return;
    const range = overrideRange || selectedRange;
    if (!range) {
      setRangeError('Select your rental start and end dates using the calendar below.');
      return;
    }

    setAdding(true);
    setStatus(null);
    try {
      await addItem({
        product_slug: product.slug,
        quantity,
        rental_start_date: range.startDate,
        rental_end_date: range.endDate
      });
      setStatus({ type: 'success', message: 'Added to cart! View your cart when you are ready to check out.' });
    } catch (err) {
      console.error('Unable to add to cart', err);
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Container maxWidth="md">
          <Typography variant="body1">Loading product…</Typography>
        </Container>
      </Page>
    );
  }

  if (error || !product) {
    return (
      <Page>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ mb: 2 }}>Product unavailable</Typography>
          <Typography variant="body1">{error || 'Try selecting another cloth from the catalog.'}</Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Box sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}>
              <Box component="img" src={selectedImage} alt={product.name} sx={{ width: '100%', display: 'block' }} />
            </Box>
            <Stack direction="row" spacing={2} sx={{ mt: 2, overflowX: 'auto' }}>
              {galleryImages.map((image, index) => (
                <Box
                  key={`${image}-${index}`}
                  component="img"
                  src={image}
                  alt={`${product.name} ${index + 1}`}
                  onClick={() => setSelectedImage(image)}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    border: selectedImage === image ? '2px solid #d28b21' : '1px solid rgba(0,0,0,0.1)',
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Header text={product.name} subHeader={product.description} align="left" />
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatPrice(product.price_cents, product.currency)}
              </Typography>
              <Chip label={`${product.rental_min_days}-day minimum`} color="secondary" />
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Rentals ship {product.shipping_window_days || 3}-5 days before your event with prepaid returns and cleaning.
            </Typography>
            <TextField
              type="number"
              label="Quantity"
              value={quantity}
              onChange={handleQuantityChange}
              inputProps={{ min: 1 }}
              sx={{ maxWidth: 160, mb: 2 }}
            />
            {selectedRange ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Rental window: {dayjs(selectedRange.startDate).format('MMM D, YYYY')} – {dayjs(selectedRange.endDate).format('MMM D, YYYY')}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Choose your dates below to update the availability.
              </Typography>
            )}
          </Grid>
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Header text="Choose your dates" subHeader="Select the arrival and return dates for this cloth" align="left" />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ flexGrow: 1 }}>
              Reserve your preferred window and we’ll ship the cloth {product.shipping_window_days || 3}-5 days before your party.
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => handleAddToCart()}
              disabled={adding}
              sx={{ borderRadius: 999, px: 4 }}
            >
              {adding ? 'Adding…' : selectedRange ? 'Add to Cart' : 'Select Dates to Add to Cart'}
            </Button>
          </Stack>
          {rangeError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {rangeError}
            </Alert>
          )}
          {status && (
            <Alert severity={status.type} sx={{ mb: 2 }}>
              {status.message}
            </Alert>
          )}
          <Box sx={{ backgroundColor: '#003a27', borderRadius: 4, p: { xs: 2, md: 4 }, color: '#fff', boxShadow: '0 30px 60px rgba(0,0,0,0.35)' }}>
            <Calendar holidays={holidays} onConfirm={handleCalendarConfirm} confirmLabel="Add to Cart" />
          </Box>
        </Box>
      </Container>
    </Page>
  );
};

export default ProductShowPage;
